import { useQuery, UseQueryOptions } from 'react-query';
import { DrifterVersionConfig } from '../models/dirfter-version.js';
import Api from '../../../api/instance.js';
import { NOOP } from '@testsigma/atoms';
import { getDrifterPortSync } from '@utils/agent-url.utils';

import {
  getDrifterClient,
  AgentStatus,
  checkDrifterHealth as requestCheckDrifterHealth,
  stopDebuggingSession as requestStopDebuggingSession,
  startDebuggingSession as requestStartDebuggingSession,
  startAgenticSession as requestStartAgenticSession,
  // stopAiWidget as requestStopAiWidget,
  startDesktopRecording as requestStartDesktopRecording,
  stopDesktopRecording as requestStopDesktopRecording,
  startDesktopStepRecording as requestStartDesktopStepRecording,
  stopDesktopStepRecording as requestStopDesktopStepRecording,
  getAllRunningApplications as requestGetAllRunningApplications,
  authorizeTestsigmaTerminal as requestAuthorizeTestsigmaTerminal,
  startMobileSession as requestStartMobileSession,
  stopMobileSession as requestStopMobileSession,
} from '@testsigmainc/grpc-web-client';

import type {
  AuthorizationRequest,
  HealthCheckResponse,
  SetUpDesktopRecordingRequest,
  SetUpDesktopStepRecordingRequest,
  StartAgenticSessionRequest,
  StartDebuggingSessionRequest,
  StopDebuggingSessionRequest,
  StartMobileSessionRequest,
} from '@testsigmainc/grpc-web-client';
// import versionsJson from './versions.json';

const protocol = 'https';
const host = import.meta.env.VITE_DEBUGGER_HOST;

/**
 * Gets the drifter port from PortConfig if available, otherwise falls back to VITE_DEBUGGER_PORT
 */

const getPort = (): string | undefined => {
  return getDrifterPortSync() || import.meta.env.VITE_DEBUGGER_PORT;
};

const authorize = (
  requestId: string,
  token: string,
  serverPort?: string,
): Promise<any> => {
  const request: AuthorizationRequest = {
    requestId: requestId,
    token: token,
    $typeName: 'drifter.AuthorizationRequest',
  };

  return requestAuthorizeTestsigmaTerminal(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: serverPort ?? getPort(),
    }),
    request,
  );
};

const startDebuggingSession = ({
  tenantId,
  versionId,
  projectId,
  applicationType,
  testCaseId,
  executionResultId,
  testCaseResultId,
  token,
  isAtto,
  attoData,
}: {
  tenantId: number;
  versionId: number;
  projectId: number;
  applicationType: string;
  testCaseId: number;
  executionResultId: number;
  testCaseResultId: number;
  token: string;
  isAtto?: boolean;
  attoData?: {
    sourceUrl: string;
    showRunningWithAtto: boolean;
    testCaseId: number;
  };
}): Promise<any> => {
  const attoDataValue = {
    sourceUrl: attoData?.sourceUrl ?? '',
    showRunningWithAtto: attoData?.showRunningWithAtto ?? false,
    testCaseId: attoData?.testCaseId ?? 0,
    $typeName: 'drifter.AttoData' as const,
  };

  const request: StartDebuggingSessionRequest = {
    tenantId: BigInt(tenantId),
    versionId: BigInt(versionId),
    projectId: BigInt(projectId),
    applicationType: applicationType,
    testCaseId: BigInt(testCaseId),
    executionResultId: BigInt(executionResultId),
    testCaseResultId: BigInt(testCaseResultId),
    token: token,
    isAtto: isAtto ?? false,
    attoData: attoDataValue,
    $typeName: 'drifter.StartDebuggingSessionRequest',
  };

  return requestStartDebuggingSession(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
    request,
  );
};

const stopDebuggingSession = (tenantId, testCaseResultId) => {
  const request: StopDebuggingSessionRequest = {
    tenantId: tenantId,
    testCaseResultId: testCaseResultId,
    $typeName: 'drifter.StopDebuggingSessionRequest',
  };
  return requestStopDebuggingSession(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
    request,
  );
};

const checkDrifterHealth = (): Promise<HealthCheckResponse> => {
  return requestCheckDrifterHealth(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
  );
};

const getAllRunningApplications = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    requestGetAllRunningApplications(
      getDrifterClient({
        protocol: protocol,
        host: host,
        port: getPort(),
      }),
    )
      .then((response) => {
        // Format the response for the RecorderModal
        if (response && response.applications) {
          const formattedApps = response.applications.map((app) => ({
            processId: app.processId,
            processName: app.processName,
            mainWindowTitle: app.mainWindowTitle,
            iconPath: app.iconPath,
          }));
          resolve(formattedApps);
        } else {
          resolve([]);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getDrifterVersionJson = (url: string) => {
  return Api.get(url)
    .then((res) => res)
    .catch(() => {});
};

interface DrifterVersionRequest {
  url: string;
  onSuccess: (res: DrifterVersionConfig) => void;
  enabled?: boolean;
}
const useQueryGetDrifterVersions = ({
  url,
  onSuccess = NOOP,
  enabled = true,
}: DrifterVersionRequest) => {
  const options: UseQueryOptions = {
    queryKey: ['DRIFTER_VERSION_QUERY_KEY', url],
    queryFn: () => getDrifterVersionJson(url),
    enabled: enabled && !!url,
    cacheTime: 0,
    staleTime: 0,
    meta: { persist: false },
    onSuccess: (res) => {
      const deserializeData = new DrifterVersionConfig().deserialize(res);
      onSuccess(deserializeData);
    },
  };
  return useQuery(options);
};

const setUpDesktopRecording = (Input) => {
  const request: SetUpDesktopRecordingRequest = Input;

  return requestStartDesktopRecording(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
    request,
  );
};

const stopDesktopRecording = (): Promise<boolean> => {
  return requestStopDesktopRecording(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
  );
};

const startAgenticSession = ({
  tenantId,
  versionId,
  projectId,
  applicationType,
  testCaseKey,
  content,
  authToken,
  targetMachine,
  attoSessionId,
}: {
  tenantId: string;
  versionId: number;
  projectId: number;
  applicationType: string;
  testCaseKey: string;
  content: any;
  authToken: string;
  targetMachine: string;
  attoSessionId?: string;
}) => {
  // Extract conversationId from content if it exists
  const conversationId = content?.conversationId;

  const contentObj: any = { content, tenantId, targetMachine };
  if (attoSessionId) {
    contentObj.attoSessionId = attoSessionId;
  }
  if (conversationId) {
    contentObj.conversationId = conversationId;
  }
  const request: StartAgenticSessionRequest = {
    tenantId: BigInt(tenantId),
    versionId: BigInt(versionId),
    projectId: BigInt(projectId),
    applicationType: applicationType,
    testCaseKey: testCaseKey,
    content: JSON.stringify(contentObj),
    authToken: authToken,
    $typeName: 'drifter.StartAgenticSessionRequest',
  };

  return requestStartAgenticSession(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
    request,
  )
    .then((response: any) => {
      return response;
    })
    .catch((error: any) => {
      throw error;
    });
};

const stopAiWidget = (): Promise<boolean> => {
  return new Promise(() => {
    // requestStopAiWidget(
    //   getDrifterClient({
    //     protocol: protocol,
    //     host: host,
    //     port: port,
    //   }),
    // )
    //   .then(() => {
    //     resolve(true);
    //   })
    //   .catch((error) => {
    //     reject(error);
    //   });
  });
};

const setUpDesktopStepRecording = (Input) => {
  const request: SetUpDesktopStepRecordingRequest = Input;
  return requestStartDesktopStepRecording(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
    request,
  );
};

const stopDesktopStepRecording = (): Promise<boolean> => {
  return requestStopDesktopStepRecording(
    getDrifterClient({
      protocol: protocol,
      host: host,
      port: getPort(),
    }),
  );
};

const startMobileSession = async ({
  tenantId,
  versionId,
  projectId,
  applicationType,
  osType,
  deviceName,
  osVersion,
  productModel,
  screenHeight,
  screenWidth,
  uniqueId,
  isEmulator,
  isOnline,
  testCaseId,
}: {
  tenantId: number;
  versionId: number;
  projectId: number;
  applicationType: string;
  osType: string;
  deviceName: string;
  osVersion: string;
  productModel: string;
  screenHeight: number;
  screenWidth: number;
  uniqueId: string;
  isEmulator: boolean;
  isOnline: boolean;
  testCaseId: number;
}): Promise<any> => {
  try {
    const request: StartMobileSessionRequest = {
      tenantId: BigInt(tenantId),
      versionId: BigInt(versionId),
      projectId: BigInt(projectId),
      applicationType: applicationType,
      osType: osType,
      deviceName: deviceName,
      osVersion: osVersion,
      productModel: productModel,
      screenHeight: screenHeight,
      screenWidth: screenWidth,
      uniqueId: uniqueId,
      isEmulator: isEmulator,
      isOnline: isOnline,
      testCaseId: BigInt(testCaseId),
      $typeName: 'drifter.StartMobileSessionRequest',
    };

    return await requestStartMobileSession(
      getDrifterClient({
        protocol: protocol,
        host: host,
        port: getPort(),
      }),
      request,
    );
  } catch (error) {
    // Gracefully handle connection errors (e.g., when Drifter is not running)
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to start mobile session (Drifter may not be available):',
      error,
    );
    return null;
  }
};

const stopMobileSession = async (): Promise<any> => {
  try {
    return await requestStopMobileSession(
      getDrifterClient({
        protocol: protocol,
        host: host,
        port: getPort(),
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error stopping mobile session:', error);
    return null;
  }
};

export const DrifterService = {
  authorize,
  useQueryGetDrifterVersions,
  startDebuggingSession,
  stopDebuggingSession,
  checkDrifterHealth,
  startAgenticSession,
  stopAiWidget,
  setUpDesktopRecording,
  stopDesktopRecording,
  setUpDesktopStepRecording,
  stopDesktopStepRecording,
  getAllRunningApplications,
  startMobileSession,
  stopMobileSession,
};
export type { HealthCheckResponse };
export { AgentStatus };
